/**
 * DiscoverProductsModal
 *
 * Modal for scanning a website to discover product URLs.
 * Allows users to select which products to import.
 */

import React, { useState } from 'react';
import {
    X,
    Search,
    Loader2,
    Globe,
    ShoppingBag,
    Check,
    ExternalLink,
    AlertCircle,
    CheckCircle
} from 'lucide-react';
import { discoverProductUrls, scanProductUrl } from '../../services/productService';

const DiscoverProductsModal = ({
    isOpen,
    onClose,
    onImport,
    websiteUrl = ''
}) => {
    const [urlInput, setUrlInput] = useState(websiteUrl);
    const [scanning, setScanning] = useState(false);
    const [scanError, setScanError] = useState(null);
    const [discoveredUrls, setDiscoveredUrls] = useState([]);
    const [selectedUrls, setSelectedUrls] = useState([]);
    const [importing, setImporting] = useState(false);
    const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });

    const handleScanWebsite = async () => {
        if (!urlInput.trim()) return;

        setScanning(true);
        setScanError(null);
        setDiscoveredUrls([]);
        setSelectedUrls([]);

        try {
            const urls = await discoverProductUrls(urlInput.trim());
            setDiscoveredUrls(urls);

            if (urls.length === 0) {
                setScanError('No product URLs found on this page. Try scanning a category or shop page.');
            }
        } catch (error) {
            console.error('Error scanning website:', error);
            setScanError('Failed to scan website. Please check the URL and try again.');
        } finally {
            setScanning(false);
        }
    };

    const handleToggleUrl = (url) => {
        setSelectedUrls(prev =>
            prev.includes(url)
                ? prev.filter(u => u !== url)
                : [...prev, url]
        );
    };

    const handleSelectAll = () => {
        if (selectedUrls.length === discoveredUrls.length) {
            setSelectedUrls([]);
        } else {
            setSelectedUrls([...discoveredUrls]);
        }
    };

    const handleImport = async () => {
        if (selectedUrls.length === 0) return;

        setImporting(true);
        setImportProgress({ current: 0, total: selectedUrls.length });

        const importedProducts = [];

        for (let i = 0; i < selectedUrls.length; i++) {
            setImportProgress({ current: i + 1, total: selectedUrls.length });

            try {
                const productData = await scanProductUrl(selectedUrls[i]);
                importedProducts.push(productData);
            } catch (error) {
                console.error('Error scanning product:', error);
                // Continue with other products even if one fails
                importedProducts.push({
                    name: `Product from ${new URL(selectedUrls[i]).hostname}`,
                    productUrl: selectedUrls[i],
                    isActive: true
                });
            }
        }

        setImporting(false);
        onImport(importedProducts);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
                padding: 'var(--space-4)'
            }}
            onClick={(e) => e.target === e.currentTarget && !scanning && !importing && onClose()}
        >
            <div
                className="card"
                style={{
                    width: '100%',
                    maxWidth: '600px',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    position: 'relative'
                }}
            >
                {/* Header */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 'var(--space-4)',
                    paddingBottom: 'var(--space-4)',
                    borderBottom: '1px solid var(--color-border)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                        <Search size={20} style={{ color: 'var(--color-primary)' }} />
                        <h3 style={{ margin: 0 }}>Discover Products</h3>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={scanning || importing}
                        className="btn btn-ghost"
                        style={{ padding: 'var(--space-1)' }}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* URL Input */}
                <div style={{
                    padding: 'var(--space-4)',
                    background: 'var(--color-bg-secondary)',
                    borderRadius: 'var(--radius-md)',
                    marginBottom: 'var(--space-4)'
                }}>
                    <label style={{
                        display: 'block',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 500,
                        marginBottom: 'var(--space-2)'
                    }}>
                        <Globe size={14} style={{ marginRight: 'var(--space-1)', verticalAlign: 'middle' }} />
                        Website or Shop URL
                    </label>
                    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                        <input
                            type="url"
                            value={urlInput}
                            onChange={(e) => setUrlInput(e.target.value)}
                            placeholder="https://yourstore.com/shop"
                            className="input"
                            style={{ flex: 1 }}
                            disabled={scanning || importing}
                        />
                        <button
                            onClick={handleScanWebsite}
                            disabled={!urlInput.trim() || scanning || importing}
                            className="btn btn-primary"
                        >
                            {scanning ? (
                                <>
                                    <Loader2 size={14} className="spin" />
                                    Scanning...
                                </>
                            ) : (
                                <>
                                    <Search size={14} />
                                    Scan
                                </>
                            )}
                        </button>
                    </div>
                    <p style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-tertiary)',
                        margin: 'var(--space-2) 0 0 0'
                    }}>
                        Enter your shop URL or a category page to discover product links.
                    </p>
                </div>

                {/* Error Message */}
                {scanError && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        padding: 'var(--space-3)',
                        background: 'var(--warning-50)',
                        borderRadius: 'var(--radius-md)',
                        marginBottom: 'var(--space-4)',
                        color: 'var(--warning-700)'
                    }}>
                        <AlertCircle size={18} />
                        <span>{scanError}</span>
                    </div>
                )}

                {/* Discovered URLs */}
                {discoveredUrls.length > 0 && (
                    <div>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: 'var(--space-3)'
                        }}>
                            <span style={{
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 500
                            }}>
                                Found {discoveredUrls.length} product URLs
                            </span>
                            <button
                                onClick={handleSelectAll}
                                className="btn btn-ghost btn-sm"
                            >
                                {selectedUrls.length === discoveredUrls.length ? 'Deselect All' : 'Select All'}
                            </button>
                        </div>

                        <div style={{
                            maxHeight: '300px',
                            overflowY: 'auto',
                            border: '1px solid var(--color-border)',
                            borderRadius: 'var(--radius-md)'
                        }}>
                            {discoveredUrls.map((url, index) => {
                                const isSelected = selectedUrls.includes(url);
                                return (
                                    <div
                                        key={index}
                                        onClick={() => handleToggleUrl(url)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-2)',
                                            padding: 'var(--space-2) var(--space-3)',
                                            background: isSelected ? 'var(--success-50)' : 'var(--color-bg-primary)',
                                            borderBottom: index < discoveredUrls.length - 1 ? '1px solid var(--color-border)' : 'none',
                                            cursor: 'pointer',
                                            transition: 'background var(--transition-fast)'
                                        }}
                                    >
                                        <div style={{
                                            width: '20px',
                                            height: '20px',
                                            borderRadius: 'var(--radius-sm)',
                                            border: `2px solid ${isSelected ? 'var(--success-500)' : 'var(--color-border)'}`,
                                            background: isSelected ? 'var(--success-500)' : 'transparent',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}>
                                            {isSelected && <Check size={12} style={{ color: 'white' }} />}
                                        </div>

                                        <div style={{
                                            flex: 1,
                                            minWidth: 0,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            fontSize: 'var(--font-size-sm)'
                                        }}>
                                            {url}
                                        </div>

                                        <a
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            style={{
                                                padding: 'var(--space-1)',
                                                color: 'var(--color-text-secondary)'
                                            }}
                                        >
                                            <ExternalLink size={14} />
                                        </a>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Import Progress */}
                {importing && (
                    <div style={{
                        marginTop: 'var(--space-4)',
                        padding: 'var(--space-4)',
                        background: 'var(--info-50)',
                        borderRadius: 'var(--radius-md)'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            marginBottom: 'var(--space-2)'
                        }}>
                            <Loader2 size={16} className="spin" style={{ color: 'var(--info-600)' }} />
                            <span style={{ color: 'var(--info-700)', fontWeight: 500 }}>
                                Importing products... ({importProgress.current}/{importProgress.total})
                            </span>
                        </div>
                        <div style={{
                            height: '6px',
                            background: 'var(--info-200)',
                            borderRadius: 'var(--radius-full)',
                            overflow: 'hidden'
                        }}>
                            <div style={{
                                height: '100%',
                                width: `${(importProgress.current / importProgress.total) * 100}%`,
                                background: 'var(--info-500)',
                                borderRadius: 'var(--radius-full)',
                                transition: 'width 0.3s ease'
                            }} />
                        </div>
                    </div>
                )}

                {/* Footer */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 'var(--space-2)',
                    marginTop: 'var(--space-4)',
                    paddingTop: 'var(--space-4)',
                    borderTop: '1px solid var(--color-border)'
                }}>
                    <button
                        onClick={onClose}
                        className="btn btn-ghost"
                        disabled={scanning || importing}
                    >
                        Cancel
                    </button>
                    {discoveredUrls.length > 0 && (
                        <button
                            onClick={handleImport}
                            className="btn btn-primary"
                            disabled={selectedUrls.length === 0 || importing}
                        >
                            {importing ? (
                                <>
                                    <Loader2 size={16} className="spin" />
                                    Importing...
                                </>
                            ) : (
                                <>
                                    <ShoppingBag size={16} />
                                    Import {selectedUrls.length} Product{selectedUrls.length !== 1 ? 's' : ''}
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DiscoverProductsModal;
